/**
 * CatchballSetupPage
 *
 * Configuration surface for a cycle's Catchball participant setup.
 * Reachable at /prioritization/cascade/setup/:cycleId and from the "Setup"
 * tab in the Catchball sticky header.
 *
 * Shows:
 *   • A mode selector: Org chart vs Custom participants
 *   • When "Custom participants" is active, two participant pickers:
 *       - Ball recipients (who receives the toss)
 *       - Default participants (who can contribute below recipients)
 *
 * Persists to catchball_virtual_configs (migration 378).
 */

import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CircleDot, Settings2 } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { CatchballStickyHeader } from '../../../components/prioritization/catchball/CatchballStickyHeader';
import { CatchballParticipantConfig } from '../../../components/prioritization/catchball/CatchballParticipantConfig';
import { BallStrip } from '../../../components/prioritization/catchball/BallStrip';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useCatchballBallStore } from '../../../stores/prioritization/catchballBallStore';

export function CatchballSetupPage() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const cycle = useDegapProcessStore((s) => s.cycles.find((c) => c.id === cycleId));
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId) ?? cycle?.workspaceId ?? '';

  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const allLoops = useCatchballLoopStore((s) => s.loops);
  const fetchBallsForCycle = useCatchballBallStore((s) => s.fetchBallsForCycle);
  const getBallsForLoop = useCatchballBallStore((s) => s.getBallsForLoop);

  useEffect(() => {
    if (cycleId) {
      fetchLoops(cycleId);
      void fetchBallsForCycle(cycleId);
    }
  }, [cycleId, fetchLoops, fetchBallsForCycle]);

  const activeLoop = useMemo(() => {
    if (!cycleId) return null;
    const cycleLoops = allLoops
      .filter((l) => l.cycleId === cycleId)
      .sort((a, b) => b.loopNumber - a.loopNumber);
    return cycleLoops.find((l) => l.state !== 'closed') ?? cycleLoops[0] ?? null;
  }, [allLoops, cycleId]);

  const activeBalls = activeLoop ? getBallsForLoop(activeLoop.id) : [];

  if (!cycleId) {
    return (
      <div className="p-6 text-sm text-[var(--coral)] dark:text-[var(--coral)]">
        No cycle ID provided.
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[var(--surface)] dark:bg-[var(--bg)]">
      <CatchballStickyHeader cycleId={cycleId} active="setup" />

      <div className="space-y-6 p-6">
        <PageHeader
          icon={<Settings2 className="h-4 w-4 text-[var(--text-ter)]" />}
          iconBg="bg-[var(--slate-dim)]"
          title="Catchball setup"
          subtitle={cycle ? `Participant configuration for ${cycle.name}` : 'Participant configuration'}
        />

        <div className="max-w-2xl">
          <CatchballParticipantConfig
            cycleId={cycleId}
            workspaceId={workspaceId}
            orgScopeNodeId={cycle?.orgScopeNodeId}
          />
        </div>

        {/* Ball roster preview — read-only; shown once balls have been created */}
        {activeLoop && activeBalls.length > 0 && (
          <div className="max-w-2xl">
            <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-5">
              <div className="flex items-center gap-2 mb-4">
                <CircleDot className="h-4 w-4 text-[var(--green)]" />
                <div>
                  <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    Ball roster — Loop {activeLoop.loopNumber}
                  </h3>
                  <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                    One Ball per recipient org unit. Each Ball carries the request downward and returns a Proposal upward.
                  </p>
                </div>
              </div>
              <BallStrip loopId={activeLoop.id} cycleId={cycleId} linkToBalls />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CatchballSetupPage;
